import React, { FC, useEffect, useState, useCallback, useRef } from 'react'
import { Button, Card, Col, Form, Row, Alert, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

// hooks
import { useApi } from 'landing-page/hooks/useApi'
import { api } from 'landing-page/helpers'
import handleError from 'utils/helpers/api/handleError'

// layout
import MainLayout from 'landing-page/components/layouts/MainLayout'

// components
import AuthModal from 'landing-page/components/profile/AuthModal'
import Section from 'landing-page/components/common/Section'
import Container from 'landing-page/components/common/Container'

// types
import { SubmitProfileAlumniRequest, BidangSektor } from 'landing-page/types/api'

const Profile: FC = () => {

    const navigate = useNavigate()

    const [showAuthModal, setShowAuthModal] = useState(false);

    const [nibNik, setNibNik] = useState('');
    
    const [alumniSearchData, setAlumniSearchData] = useState<any>(null);
    
    const [searchLoading, setSearchLoading] = useState(false);
    
    const [notFound, setNotFound] = useState(false);
    
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [bidangSektor, setBidangSektor] = useState<BidangSektor[]>([]);

    // Form states
    const [formData, setFormData] = useState<SubmitProfileAlumniRequest>({
        nib_nik: '',
        nama_lengkap: null,
        gelar_akademik_depan: '',
        gelar_akademik_belakang: '',
        foto_profil: null,
        linkedin_profile: '',
        program_beasiswa: null,
        universitas: null,
        negara: null,
        tahun_lulus: null,
        bidang_studi: null,
        pekerjaan_saat_ini: null,
        perusahaan_instansi: null,
        prestasi_utama: null,
        kontribusi_masyarakat: null,
        biography: null,
        bidang_sektor: null,
        deskripsi_singkat_pekerjaan: null,
        kutipan_inspiratif: null,
        pesan_untuk_calon_awardee: null,
        kenangan_paling_berkesan_studi: null,
        is_setuju: 0,
        submit_by_admin: false
    });

    // Form validation states
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [submitError, setSubmitError] = useState<string | null>(null);
    
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Handle success login
    const handleSuccess = () => {
        setShowAuthModal(false)
        // Retry getting profile data after successful login
        executeGetProfileAlumni()
    }

    // Get profile alumni
    const { data, execute: executeGetProfileAlumni, loading, error, reset: resetGetProfileAlumni } = useApi(api.getProfile.bind(api));

    // Get Bidang Sektor
    const { data: bidangSektorData, execute: executeGetBidangSektor, loading: bidangSektorLoading, error: bidangSektorError, reset: resetGetBidangSektor } = useApi(api.getBidangSektor.bind(api));

    // Debounced search function
    const debouncedSearch = useCallback((value: string) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
            if (value.length >= 12 && /^\d+$/.test(value)) {
                searchAlumni(value);
            } else {
                setAlumniSearchData(null)
            }
        }, 500);
    }, []);

    // Search alumni function
    const searchAlumni = async (nibNikValue: string) => {
        if (nibNikValue.length < 12 || !/^\d+$/.test(nibNikValue)) {
            return;
        }

        setSearchLoading(true);
        try {
            const response = await api.searchAlumni({ nib_nik: nibNikValue });
            
            if (response.meta.code === 200 && response.data) {
                setAlumniSearchData(response.data);
                setNotFound(false);
                // Update form data with found alumni
                setFormData(prev => ({
                    ...prev,
                    nib_nik: nibNikValue,
                    nama_lengkap: response.data?.nama_lengkap || null,
                }));
            } else {
                setAlumniSearchData(null);
            }
        } catch (error: any) {

            if (error.status === 400 && error.message === 'Data tidak ditemukan') {
                setNotFound(true);
            }

            setAlumniSearchData(null);
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle NIB/NIK input change
    const handleNibNikChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        
        // Only allow numeric input
        if (!/^\d*$/.test(value)) {
            return;
        }
        
        setNibNik(value);
        debouncedSearch(value);
    };

    // Handle form input changes
    const handleInputChange = (field: keyof SubmitProfileAlumniRequest, value: any) => {
        if (field === 'is_setuju') {
            value = value ? 1 : 0;
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Handle file upload
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (2MB)
            if (file.size > 2 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    foto_profil: 'Ukuran file maksimal 2MB'
                }));
                return;
            }

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                setErrors(prev => ({
                    ...prev,
                    foto_profil: 'Format file harus JPG, JPEG, atau PNG'
                }));
                return;
            }

            handleInputChange('foto_profil', file);
            setErrors(prev => ({
                ...prev,
                foto_profil: ''
            }));
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Required fields validation
        if (!formData.nib_nik) {
            newErrors.nib_nik = 'NIB/NIK wajib diisi';
        }

        // if (!formData.gelar_akademik_depan) {
        //     newErrors.gelar_akademik_depan = 'Gelar akademik depan wajib diisi';
        // }

        if (!formData.gelar_akademik_belakang) {
            newErrors.gelar_akademik_belakang = 'Gelar akademik belakang wajib diisi';
        }

        // if (!formData.foto_profil) {
        //     newErrors.foto_profil = 'Foto profil wajib diupload';
        // }

        if (!formData.linkedin_profile) {
            newErrors.linkedin_profile = 'Tautan LinkedIn wajib diisi';
        } else if (!formData.linkedin_profile.includes('linkedin.com')) {
            newErrors.linkedin_profile = 'Tautan harus berupa URL LinkedIn yang valid';
        }

        if (!formData.pekerjaan_saat_ini) {
            newErrors.pekerjaan_saat_ini = 'Pekerjaan saat ini wajib diisi';
        }

        if (!formData.perusahaan_instansi) {
            newErrors.perusahaan_instansi = 'Perusahaan/Instansi wajib diisi';
        }

        if (!formData.is_setuju) {
            newErrors.is_setuju = 'Anda harus menyetujui data dan foto dalam formulir ini dipublikasikan pada Flipbook Profil Alumni LPDP.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();
            
            // Append all form fields
            formDataToSend.append('nib_nik', formData.nib_nik);
            if (formData.nama_lengkap) {
                formDataToSend.append('nama_lengkap', formData.nama_lengkap);
            }
            formDataToSend.append('gelar_akademik_depan', formData.gelar_akademik_depan);
            formDataToSend.append('gelar_akademik_belakang', formData.gelar_akademik_belakang);
            
            // Append file if exists
            if (formData.foto_profil) {
                formDataToSend.append('foto_profil', formData.foto_profil);
            }
            
            formDataToSend.append('linkedin_profile', formData.linkedin_profile);
            
            // Append optional fields
            if (formData.program_beasiswa) {
                formDataToSend.append('program_beasiswa', formData.program_beasiswa);
            }
            if (formData.universitas) {
                formDataToSend.append('universitas', formData.universitas);
            }
            if (formData.negara) {
                formDataToSend.append('negara', formData.negara);
            }
            if (formData.tahun_lulus) {
                formDataToSend.append('tahun_lulus', formData.tahun_lulus.toString());
            }
            if (formData.bidang_studi) {
                formDataToSend.append('bidang_studi', formData.bidang_studi);
            }
            if (formData.pekerjaan_saat_ini) {
                formDataToSend.append('pekerjaan_saat_ini', formData.pekerjaan_saat_ini);
            }
            if (formData.perusahaan_instansi) {
                formDataToSend.append('perusahaan_instansi', formData.perusahaan_instansi);
            }
            if (formData.prestasi_utama) {
                formDataToSend.append('prestasi_utama', formData.prestasi_utama);
            }
            if (formData.kontribusi_masyarakat) {
                formDataToSend.append('kontribusi_masyarakat', formData.kontribusi_masyarakat);
            }
            if (formData.biography) {
                formDataToSend.append('biography', formData.biography);
            }
            if (formData.bidang_sektor) {
                formDataToSend.append('bidang_sektor', formData.bidang_sektor);
            }
            if (formData.deskripsi_singkat_pekerjaan) {
                formDataToSend.append('deskripsi_singkat_pekerjaan', formData.deskripsi_singkat_pekerjaan);
            }
            if (formData.kutipan_inspiratif) {
                formDataToSend.append('kutipan_inspiratif', formData.kutipan_inspiratif);
            }
            if (formData.pesan_untuk_calon_awardee) {
                formDataToSend.append('pesan_untuk_calon_awardee', formData.pesan_untuk_calon_awardee);
            }
            if (formData.kenangan_paling_berkesan_studi) {
                formDataToSend.append('kenangan_paling_berkesan_studi', formData.kenangan_paling_berkesan_studi);
            }

            formDataToSend.append('is_setuju', formData.is_setuju.toString());
            formDataToSend.append('submit_by_admin', (localStorage.getItem('is_umum') === '1' ? true : false).toString());

            const response = await api.submitProfileAlumni(formDataToSend);
            
            setSubmitSuccess(true);
            
            // Redirect after 5 seconds
            setTimeout(() => {
                navigate('/riwayat-pengajuan');
            }, 5000);
            
        } catch (error: any) {
            console.error('Error submitting profile:', error);
            
            if (error.status === 401) {
                setShowAuthModal(true);
            } else if (error.status === 400) {
                const response = error.response;
                const mssg = response?.data?.meta?.message;
                const errorDetails = response?.data?.meta?.error;

                if (errorDetails) {
                    // Loop through error details and display each with toast
                    Object.keys(errorDetails).forEach(function (key, index) {
                        setTimeout(() => {
                            toast.error(errorDetails[key][0])
                        }, index === 0 ? 0 : 200)
                    });
                    
                    // Create detailed error message for setSubmitError
                    const detailedErrors = Object.keys(errorDetails).map(key => 
                        `• ${errorDetails[key][0]}`
                    ).join('\n');
                    
                    setSubmitError(`${mssg || 'Terjadi kesalahan saat mengirim profil:'}\n\n${detailedErrors}`);
                } else {
                    toast.error(mssg || 'Terjadi kesalahan saat mengirim profil. Silakan coba lagi.');
                    setSubmitError(mssg || 'Terjadi kesalahan saat mengirim profil. Silakan coba lagi.');
                }
            } else {
                setSubmitError(error.message || 'Terjadi kesalahan saat mengirim profil. Silakan coba lagi.');
            }

            handleError(error)
        } finally {
            setIsSubmitting(false);

            // scroll to top
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    // Get profile alumni on mount
    useEffect(() => {
        executeGetProfileAlumni()
    }, [])

    // Get bidang sektor if search alumni is success
    useEffect(() => {
        if (alumniSearchData) {
            executeGetBidangSektor()
        }
    }, [alumniSearchData])

    // Update bidang sektor when data is available
    useEffect(() => {
        if (bidangSektorData?.data?.items) {
            setBidangSektor(bidangSektorData.data.items as BidangSektor[])
        }
    }, [bidangSektorData])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Handle expired token
    useEffect(() => {
        if (error) {
            
            // Check if it's a 401 error with "Token kadaluarsa" message
            if (error.status === 401 && error.message === 'Token kadaluarsa.') {
                // Clear the expired token
                api.clearToken();
                // Show login modal
                setShowAuthModal(true);
            }

            if (error.status === 401 && error.message === 'Token wajib diisi.') {
                // Clear the expired token
                api.clearToken();
                // Show login modal 
                setShowAuthModal(true);
            }
        }
    }, [error]);

    const headerProps = {
        user: data?.data // user from API
    }

    return (
        <>
            <AuthModal
                show={showAuthModal}
                onHide={() => setShowAuthModal(false)}
                onSuccess={handleSuccess}
            />
            
            <MainLayout headerProps={headerProps}>
                <Section className='py-15 mt-15' variant='white'>
                    <Container>
                        <div className='mb-10'>
                            <h1 className='fs-1 m-0' style={{textTransform: 'unset', lineHeight: '2'}}>Ajukan Profil Anda</h1>
                            <p className='fs-5'>Lengkapi informasi berikut untuk menampilkan perjalanan, prestasi, dan kontribusi Anda di halaman Featured Alumni LPDP.</p>
                        </div>

                        {submitSuccess && (
                            <Alert variant="success" className="mb-4">
                                <Alert.Heading>Berhasil!</Alert.Heading>
                                <p>
                                    Profil Anda berhasil dikirim. Anda akan dialihkan ke halaman pengajuan profil dalam beberapa detik.
                                </p>
                            </Alert>
                        )}

                        {submitError && (
                            <Alert variant="danger" className="mb-4" dismissible onClose={() => setSubmitError(null)}>
                                <Alert.Heading>Error!</Alert.Heading>
                                <p>{submitError}</p>
                            </Alert>
                        )}

                        <Form onSubmit={handleSubmit}>
                            <Card className='rounded-4 overflow-hidden shadow-sm mb-10'>
                                <Card.Body>
                                    <Card.Title className='mb-4'>Informasi Alumni yang diajukan</Card.Title>
                                    
                                    <Form.Group className='mb-3'>
                                        <Form.Label>NIB/NIK <span className='text-danger'>*</span></Form.Label>
                                        <Form.Control 
                                            type='text' 
                                            placeholder='Masukkan Nomor Induk Beasiswa atau NIK' 
                                            value={nibNik}
                                            onChange={handleNibNikChange}
                                            isInvalid={!!errors.nib_nik}
                                        />
                                        {searchLoading && (
                                            <Form.Text className="text-muted mt-2">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Mencari data alumni...
                                            </Form.Text>
                                        )}
                                        {nibNik.length > 0 && nibNik.length < 12 && (
                                            <Form.Control.Feedback type="invalid">
                                                NIB/NIK harus minimal 12 digit angka
                                            </Form.Control.Feedback>
                                        )}
                                        {errors.nib_nik && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nib_nik}
                                            </Form.Control.Feedback>
                                        )}
                                        {nibNik.length >= 12 && !/^\d+$/.test(nibNik) && (
                                            <Form.Control.Feedback type="invalid">
                                                NIB/NIK hanya boleh berisi angka
                                            </Form.Control.Feedback>
                                        )}
                                        {notFound && (
                                            <p className='text-danger mt-2'>Input yang Anda masukkan tidak terdaftar sebagai alumni LPDP. Periksa kembali atau hubungi admin jika menurut Anda ini keliru.</p>
                                        )}
                                    </Form.Group>

                                    {alumniSearchData && (
                                        <>
                                            <Form.Group className='mb-3'>
                                                <Form.Label>Nama Lengkap <span className='text-danger'>*</span></Form.Label>
                                                <Form.Control 
                                                    type='text' 
                                                    placeholder='Masukkan nama lengkap' 
                                                    value={alumniSearchData.nama_lengkap || ''}
                                                    readOnly
                                                />
                                                <Form.Text className="text-success mt-2">
                                                    Data alumni ditemukan: {alumniSearchData.nama_lengkap} (NIK: {alumniSearchData.nik})
                                                </Form.Text>
                                            </Form.Group>

                                            <Form.Group className='mb-3'>
                                                <Form.Label>Upload Foto Profil <span className='text-danger'>*</span></Form.Label>
                                                <Form.Control 
                                                    type='file' 
                                                    placeholder='Upload Foto Profil' 
                                                    className='mb-2'
                                                    onChange={handleFileChange}
                                                    isInvalid={!!errors.foto_profil}
                                                />
                                                {errors.foto_profil && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.foto_profil}
                                                    </Form.Control.Feedback>
                                                )}
                                                <Form.Text id="passwordHelpBlock" muted>
                                                    <p>Gunakan foto formal dengan ketentuan sebagai berikut:</p>
                                                    <ul>
                                                        <li>📏 Minimal: 600 × 800 px</li>
                                                        <li>📄 Format: .JPG atau .PNG</li>
                                                        <li>📦 Ukuran Maksimal: 5 MB</li>
                                                        <li>✅ Background polos atau lingkungan profesional</li>
                                                        <li>❌ Hindari foto buram, filter berlebihan, atau foto grup</li>
                                                    </ul>
                                                    <p>💡 Tips: Gunakan pencahayaan natural dan pakaian formal agar profil Anda terlihat rapi di halaman Featured Alumni LPDP.</p>
                                                </Form.Text>
                                            </Form.Group>

                                            <Form.Group className='mb-3'>
                                                <Form.Label>Tautan Profil LinkedIn <span className='text-danger'>*</span></Form.Label>
                                                <Form.Control 
                                                    type='text' 
                                                    placeholder='Contoh: https://www.linkedin.com/in/username'
                                                    value={formData.linkedin_profile}
                                                    onChange={(e) => handleInputChange('linkedin_profile', e.target.value)}
                                                    isInvalid={!!errors.linkedin_profile}
                                                />
                                                {errors.linkedin_profile && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.linkedin_profile}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>

                            {alumniSearchData && alumniSearchData.role === 'ALUMNI' && (
                                <Card className='rounded-4 overflow-hidden shadow-sm mb-10'>
                                    <Card.Body>
                                        <Card.Title className='mb-4'>Informasi Akademik</Card.Title>
                                        <Form.Group className='mb-3'>
                                            <Form.Label>Program Beasiswa</Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                placeholder='Program beasiswa' 
                                                value={alumniSearchData.program_beasiswa || '-'}
                                                disabled
                                            />
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Universitas</Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                placeholder='Universitas' 
                                                value={alumniSearchData.universitas || '-'}
                                                disabled
                                            />
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Negara</Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                placeholder='Negara' 
                                                value={alumniSearchData.negara || '-'}
                                                disabled
                                            />
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Tahun Lulus</Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                placeholder='Tahun lulus' 
                                                value={alumniSearchData.tahun_lulus || '-'}
                                                disabled
                                            />
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Jenjang Studi</Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                placeholder='Jenjang studi' 
                                                value={alumniSearchData.jenjang_studi || '-'}
                                                disabled
                                            />
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            )}

                            {alumniSearchData && (
                                <div className='mb-10'>
                                    <h1 className='fs-1 m-0' style={{textTransform: 'unset', lineHeight: '2'}}>
                                        Informasi Profesional
                                    </h1>
                                    <p className='fs-5'>Infomasi ini akan ditampilkan di profil publik alumni.</p>
                                </div>
                            )}

                            {alumniSearchData && (
                                <Card className='rounded-4 overflow-hidden shadow-sm mb-10'>
                                    <Card.Body>
                                        <Card.Title className='mb-4'>Informasi Profesional</Card.Title>
                                        
                                        <Row className='mb-3'>
                                            <Col md={6}>
                                                <Form.Group className='mb-3'>
                                                    <Form.Label>Gelar Akademik Depan</Form.Label>
                                                    <Form.Control 
                                                        type='text' 
                                                        placeholder='Contoh: Prof atau Dr.'
                                                        value={formData.gelar_akademik_depan}
                                                        onChange={(e) => handleInputChange('gelar_akademik_depan', e.target.value)}
                                                        isInvalid={!!errors.gelar_akademik_depan}
                                                    />
                                                    {errors.gelar_akademik_depan && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.gelar_akademik_depan}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className='mb-3'>
                                                    <Form.Label>Gelar Akademik Belakang <span className='text-danger'>*</span></Form.Label>
                                                    <Form.Control 
                                                        type='text' 
                                                        placeholder='Contoh: S.H, S.E, S.Kom, dll.'
                                                        value={formData.gelar_akademik_belakang}
                                                        onChange={(e) => handleInputChange('gelar_akademik_belakang', e.target.value)}
                                                        isInvalid={!!errors.gelar_akademik_belakang}
                                                    />
                                                    {errors.gelar_akademik_belakang && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.gelar_akademik_belakang}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className='mb-3'>
                                                    <Form.Label>Pekerjaan Saat Ini <span className='text-danger'>*</span></Form.Label>
                                                    <Form.Control 
                                                        type='text' 
                                                        placeholder='Contoh: Dokter, Pengusaha, Pengacara, dll.'
                                                        value={formData.pekerjaan_saat_ini || ''}
                                                        onChange={(e) => handleInputChange('pekerjaan_saat_ini', e.target.value)}
                                                        isInvalid={!!errors.pekerjaan_saat_ini}
                                                    />
                                                    {errors.pekerjaan_saat_ini && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.pekerjaan_saat_ini}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className='mb-3'>
                                                    <Form.Label>Perusahaan/Instansi <span className='text-danger'>*</span></Form.Label>
                                                    <Form.Control 
                                                        type='text' 
                                                        placeholder='Contoh: Rumah Sakit, Pemerintah, Perusahaan Swasta, dll.'
                                                        value={formData.perusahaan_instansi || ''}
                                                        onChange={(e) => handleInputChange('perusahaan_instansi', e.target.value)}
                                                        isInvalid={!!errors.perusahaan_instansi}
                                                    />
                                                    {errors.perusahaan_instansi && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.perusahaan_instansi}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Bidang/Sektor</Form.Label>
                                            <Form.Control 
                                                as='select' 
                                                value={formData.bidang_sektor || ''}
                                                onChange={(e) => handleInputChange('bidang_sektor', e.target.value)}
                                                isInvalid={!!errors.bidang_sektor}
                                            >
                                                <option value=''>Pilih Bidang/Sektor</option>
                                                {bidangSektorLoading ? (
                                                    <option disabled>Loading...</option>
                                                ) : bidangSektor?.map((item: BidangSektor) => (
                                                    <option key={item.value} value={item.value}>{item.label}</option>
                                                ))}
                                            </Form.Control>
                                            {bidangSektorError && (
                                                <Form.Text className="text-danger">
                                                    Error loading bidang sektor: {bidangSektorError.message}
                                                </Form.Text>
                                            )}
                                            {errors.bidang_sektor && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.bidang_sektor}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Deskripsi Singkat Pekerjaan (2-3 Kalimat)</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Ringkas peran utama dan dampak. Contoh: “Merancang dan mengevaluasi kebijakan fiskal untuk mendukung transisi energi terbarukan di Indonesia; berkolaborasi dengan lembaga internasional.'
                                                value={formData.deskripsi_singkat_pekerjaan || ''}
                                                onChange={(e) => handleInputChange('deskripsi_singkat_pekerjaan', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Maks 300 karakter. Fokus pada dampak.</p>
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Biografi Singkat</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Ceritakan perjalanan singkat sejak lulus hingga posisi saat ini (1–2 paragraf).'
                                                value={formData.biography || ''}
                                                onChange={(e) => handleInputChange('biography', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Maks 1.000 karakter.</p>
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Prestasi Utama (Opsional)</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Sebutkan 1–3 prestasi paling membanggakan. Contoh: “TOP 45 Inovasi Pelayanan Publik 2024.'
                                                value={formData.prestasi_utama || ''}
                                                onChange={(e) => handleInputChange('prestasi_utama', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Gunakana titik koma ( ; ) untuk memisahkan setiap pencapaian.</p>
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Kontribusi untuk Masyarakat (Opsional)</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Dampak konkret yang Anda berikan (misal jumlah penerima manfaat, kebijakan yang berubah, publikasi, dsb.)'
                                                value={formData.kontribusi_masyarakat || ''}
                                                onChange={(e) => handleInputChange('kontribusi_masyarakat', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Maks 700 karakter.</p>
                                            </Form.Text>
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            )}

                            {alumniSearchData && (
                                <div className='mb-10'>
                                    <h1 className='fs-1 m-0' style={{textTransform: 'unset', lineHeight: '2'}}>
                                        Visi, Inspirasi & Personal Touch
                                    </h1>
                                    <p className='fs-5'>Juga ditampilkan di profil publik.</p>
                                </div>
                            )}

                            {alumniSearchData && (
                                <Card className='rounded-4 overflow-hidden shadow-sm mb-10'>
                                    <Card.Body>
                                        <Form.Group className='mb-3'>
                                            <Form.Label>Kutipan Inspiratif (Quote)</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Contoh: “Ilmu itu amal, dan amal itu ilmu.”'
                                                value={formData.kutipan_inspiratif || ''}
                                                onChange={(e) => handleInputChange('kutipan_inspiratif', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Pesan untuk Calon Awardee LPDP (Opsional)</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Berikan 1-2 saran singkat dan membangun.'
                                                value={formData.pesan_untuk_calon_awardee || ''}
                                                onChange={(e) => handleInputChange('pesan_untuk_calon_awardee', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Berikan 1–2 kalimat saran singkat yang jujur dan membangun.</p>
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className='mb-3'>
                                            <Form.Label>Kenangan Paling Berkesan Selama Studi</Form.Label>
                                            <Form.Control 
                                                as='textarea' 
                                                rows={5} 
                                                placeholder='Ceritakan 1–3 kenangan paling berkesan selama studi.'
                                                value={formData.kenangan_paling_berkesan_studi || ''}
                                                onChange={(e) => handleInputChange('kenangan_paling_berkesan_studi', e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                <p>Cerita singkat tentang tantangan/pertumbuhan personal yang Anda alami..</p>
                                            </Form.Text>
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            )}

                            <div className='d-flex flex-column gap-3'>
                                <Form.Group className='mb-3'>
                                    <Form.Check
                                        type='checkbox'
                                        id='agreement'
                                        label='Saya menyetujui data dan foto dalam formulir ini dipublikasikan pada Flipbook Profil Alumni LPDP.'
                                        checked={formData.is_setuju === 1}
                                        onChange={(e) => handleInputChange('is_setuju', e.target.checked ? 1 : 0)}
                                        feedback={errors.is_setuju}
                                    />
                                </Form.Group>

                                <Button 
                                    type="submit"
                                    variant='primary' 
                                    size='lg' 
                                    className='rounded-pill me-2 d-block w-100'
                                    disabled={isSubmitting || !alumniSearchData}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Mengirim Profil...
                                        </>
                                    ) : (
                                        'Kirim Profil'
                                    )}
                                </Button>
                                <Button 
                                    onClick={() => navigate('/')} 
                                    variant='outline-primary' 
                                    size='lg' 
                                    className='rounded-pill mb-2 d-block w-100'
                                    disabled={isSubmitting}
                                >
                                    Batal & Kembali
                                </Button>
                            </div>
                        </Form>
                    </Container>
                </Section>
            </MainLayout>
        </>
    )
}

export default Profile